import {
  Card,
  CardContent,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { CommonHelper } from 'src/utils/commonHelper';
const TableDonutChart = ({ title, labels, value, engagementPostType }) => {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [chartOptions, setChartOptions] = useState({
    chart: {
      type: 'donut',
      background: 'transparent',
      fontFamily: theme.typography.fontFamily,
      dropShadow: {
        enabled: true,
        top: 8,
        left: 0,
        blur: 10,
        opacity: 0.15,
        color: '#000'
      },
      animations: {
        enabled: false
      },
      toolbar: { show: false },
      sparkline: { enabled: false },
    },
    colors: [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      '#8884d8',
      '#82ca9d'
    ],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'radial',
        shadeIntensity: 0.4,
        gradientToColors: undefined,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.8,
        stops: [0, 100]
      },
    },
    labels: labels,
    plotOptions: {
      pie: {
        customScale: 0.95,
        donut: {
          size: '70%',
          background: 'transparent',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '14px',
              fontWeight: '600',
              color: theme.palette.text.primary,
              offsetY: -5
            },
            value: {
              show: true,
              fontSize: '12px',
              fontWeight: '500',
              color: theme.palette.text.secondary,
              offsetY: 8,
              formatter: (val) => `${parseFloat(val).toFixed(1)}%`,
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              fontSize: '12px',
              fontWeight: '500',
              color: theme.palette.text.secondary,
              formatter: function (w) {
                return '100%';
              }
            }
          },
        },
        expandOnClick: false,
        offsetY: 0
      },
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val > 5 ? `${val.toFixed(1)}%` : '';
      },
      style: {
        fontSize: '11px',
        fontWeight: '600',
        colors: ['#fff']
      },
      dropShadow: {
        enabled: true,
        top: 2,
        left: 2,
        blur: 3,
        opacity: 0.9,
        color: '#000'
      }
    },
    legend: {
      show: true,
      position: 'right',
      fontSize: '12px',
      fontWeight: 500,
      labels: {
        colors: theme.palette.text.primary,
        useSeriesColors: false,
      },
      markers: {
        width: 10,
        height: 10,
        radius: 3,
        strokeWidth: 2,
        strokeColor: '#fff'
      },
      itemMargin: {
        horizontal: 8,
        vertical: 4,
      },
    },
    tooltip: {
      enabled: true,
      theme: theme.palette.mode,
      style: {
        fontSize: '12px',
        fontWeight: 500,
      },
      y: {
        formatter: function (val) {
          return `${val.toFixed(1)}%`;
        },
      },
    },
    stroke: {
      show: true,
      width: 3,
      colors: [theme.palette.background.paper],
      lineCap: 'round'
    },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: {
            width: 320,
            height: 300,
          },
          legend: {
            fontSize: '11px',
            position: 'bottom',
          },
        },
      },
      {
        breakpoint: 600,
        options: {
          chart: {
            width: 280,
            height: 280,
          },
          legend: {
            fontSize: '10px',
            position: 'bottom',
          },
        },
      },
    ],
  });

  const [chartSeries, setChartSeries] = useState([]);

  useEffect(() => {
    if (value.every((val) => val === 0)) {
      setChartSeries([0, 100]);
    } else {
      setChartSeries(value);
    }
  }, [value]);

  useEffect(() => {
    setChartOptions(prev => ({
      ...prev,
      labels: labels,
      colors: [
        theme.palette.primary.main,
        theme.palette.secondary.main,
        theme.palette.success.main,
        theme.palette.warning.main,
        theme.palette.error.main,
        theme.palette.info.main,
        '#8884d8',
        '#82ca9d'
      ]
    }));
  }, [labels, theme]);

  return (
    <Card 
      sx={{ 
        width: '100%', 
        height: '100%',
        borderRadius: '16px', 
        background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid #f0f0f0',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          transform: 'translateY(-2px)'
        },
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            mb: 3,
            textAlign: 'center',
            lineHeight: 1.3
          }}
        >
          {title}
        </Typography>
        
        <Stack 
          direction="column" 
          spacing={2} 
          sx={{ 
            flex: 1,
            alignItems: 'center',
            justifyContent: 'flex-start',
            width: '100%',
            height: '100%'
          }}
        >
          {/* Chart Section */}
          <Stack 
            sx={{ 
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: 'auto',
              mb: 12
            }}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}>
              <Chart 
                options={chartOptions} 
                series={chartSeries} 
                type="donut" 
                width={isMobile ? 320 : 400} 
                height={isMobile ? 300 : 350}
              />
            </div>
          </Stack>

          {/* Table Section */}
          <Stack sx={{ flex: 1, width: '100%', minHeight: 0 }}>
            <TableContainer 
              component={Paper} 
              sx={{ 
                borderRadius: '12px', 
                background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)',
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.06)',
                overflow: 'hidden',
                height: '100%',
                width: '100%'
              }}
            >
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'text.primary', 
                        fontSize: '0.75rem',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderBottom: '2px solid rgba(25, 118, 210, 0.2)',
                        py: 1,
                        px: 1.5,
                        textAlign: 'left',
                        width: '50%'
                      }}
                    >
                      {translate('report.type')}
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'text.primary', 
                        fontSize: '0.75rem',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderBottom: '2px solid rgba(25, 118, 210, 0.2)',
                        py: 1,
                        px: 1.5,
                        width: '25%'
                      }}
                    >
                      {translate('report.engagement')}
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'text.primary', 
                        fontSize: '0.75rem',
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderBottom: '2px solid rgba(25, 118, 210, 0.2)',
                        py: 1,
                        px: 1.5,
                        width: '25%'
                      }}
                    >
                      {translate('report.percentage')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {engagementPostType &&
                    engagementPostType.map((data, index) => (
                      <TableRow 
                        key={index} 
                        sx={{ 
                          backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.02)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.04)',
                            transform: 'translateX(2px)'
                          }
                        }}
                      >
                        <TableCell 
                          sx={{ 
                            fontWeight: 500, 
                            color: 'text.primary', 
                            fontSize: '0.75rem',
                            py: 1,
                            px: 1.5,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                            textAlign: 'left',
                            width: '50%',
                            maxWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word'
                          }}
                        >
                          {data.type}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          sx={{ 
                            fontWeight: 600, 
                            color: 'primary.main',
                            fontSize: '0.75rem',
                            py: 1,
                            px: 1.5,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                            width: '25%'
                          }}
                        >
                          {CommonHelper.formatNumber(data.engagement)}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          sx={{ 
                            fontWeight: 600, 
                            color: 'success.main',
                            fontSize: '0.75rem',
                            py: 1,
                            px: 1.5,
                            borderBottom: '1px solid rgba(0,0,0,0.06)',
                            width: '25%'
                          }}
                        >
                          {data.percentage}%
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default TableDonutChart;
